import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { chatAPI } from '@/services/api';
import { Button } from '@/components/ui/button';
import { 
  Flame, ChevronRight, Target, LogOut, AlertCircle, RefreshCw, 
  X, Play, Clock, Zap, BookOpen, Trophy,
  Calendar, Star, MessageCircle, ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '@/utils/errorHandler';

const API = process.env.REACT_APP_BACKEND_URL;

// Smooth number animation component
const AnimatedNumber = ({ value, suffix = '' }) => {
  const [displayValue, setDisplayValue] = useState(0);
  
  useEffect(() => {
    const duration = 1000;
    const steps = 30;
    const increment = value / steps;
    let current = 0;
    
    const timer = setInterval(() => {
      current += increment;
      if (current >= value) {
        setDisplayValue(value);
        clearInterval(timer);
      } else {
        setDisplayValue(Math.floor(current));
      }
    }, duration / steps);
    
    return () => clearInterval(timer);
  }, [value]);
  
  return <span>{displayValue}{suffix}</span>;
};

// Progress ring component
const ProgressRing = ({ progress, size = 60, strokeWidth = 6, color = '#D946EF' }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (progress / 100) * circumference;
  
  return (
    <svg width={size} height={size} className="transform -rotate-90">
      <circle
        stroke="#E5E7EB"
        fill="transparent"
        strokeWidth={strokeWidth}
        r={radius}
        cx={size / 2}
        cy={size / 2}
      />
      <motion.circle
        stroke={color}
        fill="transparent"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        r={radius}
        cx={size / 2}
        cy={size / 2}
        initial={{ strokeDashoffset: circumference }}
        animate={{ strokeDashoffset: offset }}
        transition={{ duration: 1, ease: "easeOut" }}
        style={{
          strokeDasharray: circumference,
        }}
      />
    </svg>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [showWelcomeCard, setShowWelcomeCard] = useState(false);
  const [startingChat, setStartingChat] = useState(false);
  
  // Last conversation state (for "Continue Conversation")
  const [lastConversation, setLastConversation] = useState(null);
  const [loadingLastConv, setLoadingLastConv] = useState(true);
  
  // Gamification state
  const [gamificationStats, setGamificationStats] = useState(null);
  const [dailyMissions, setDailyMissions] = useState(null);
  const [weeklyProgress, setWeeklyProgress] = useState([0, 0, 0, 0, 0, 0, 0]);

  useEffect(() => {
    fetchData();
    fetchLastConversation();
    
    const isNewUser = localStorage.getItem('vocalira_welcomed') !== 'true';
    const fromOnboarding = searchParams.get('welcome') === 'true';
    
    if (isNewUser || fromOnboarding) {
      setShowWelcomeCard(true);
      localStorage.setItem('vocalira_welcomed', 'true');
    }
  }, [searchParams]);

  const fetchLastConversation = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/chat/last-conversation`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.has_conversation) {
          setLastConversation(data.conversation);
        }
      }
    } catch (error) {
      console.error('Error fetching last conversation:', error);
    } finally {
      setLoadingLastConv(false);
    }
  };

  const fetchData = async () => {
    setLoadError(false);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [statsRes, missionsRes] = await Promise.all([
        fetch(`${API}/api/gamification/stats`, { headers }).catch(() => ({ ok: false })),
        fetch(`${API}/api/gamification/missions`, { headers }).catch(() => ({ ok: false }))
      ]);
      
      if (statsRes.ok) {
        const stats = await statsRes.json();
        setGamificationStats(stats);
        // Simulate weekly progress based on streak
        const streak = stats?.streak?.current || 0;
        const days = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
        const today = new Date().getDay();
        const progress = days.map((_, i) => {
          if (i <= today && i > today - streak) return Math.floor(Math.random() * 30) + 10;
          return 0;
        });
        setWeeklyProgress(progress);
      }
      
      if (missionsRes.ok) {
        const missions = await missionsRes.json();
        setDailyMissions(missions);
      }
    } catch (error) {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  // MAIN CTA: Start or Continue conversation (no extra clicks!)
  const handleMainCTA = async () => {
    setStartingChat(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/chat/start-or-continue`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        navigate(`/chat/${data.conversation_id}`);
      } else {
        throw new Error('Failed to start conversation');
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
      setStartingChat(false);
    }
  };

  // Continue specific conversation
  const handleContinueConversation = () => {
    if (lastConversation?.id) {
      navigate(`/chat/${lastConversation.id}`);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Até logo!');
  };

  // Stats with defaults
  const streak = gamificationStats?.streak?.current || 0;
  const maxStreak = gamificationStats?.streak?.max || 0;
  const xp = gamificationStats?.xp || 0;
  const level = gamificationStats?.level?.level || 1;
  const levelName = gamificationStats?.level?.name || 'Iniciante';
  const levelProgress = gamificationStats?.level?.progress || 0;
  const wordsLearned = gamificationStats?.words_learned || Math.floor(xp / 10);
  const minutesPracticed = gamificationStats?.minutes_practiced || Math.floor(xp / 5);
  const conversationsCount = gamificationStats?.conversations || Math.floor(xp / 20);
  
  // Missions
  const completedMissions = dailyMissions?.missions?.filter(m => m.completed)?.length || 0;
  const totalMissions = dailyMissions?.missions?.length || 3;
  const missionsProgress = (completedMissions / totalMissions) * 100;

  const firstName = user?.name?.split(' ')[0] || 'Estudante';
  const targetLanguage = user?.target_language || user?.learning_language || 'Inglês';

  // Time-based greeting
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Bom dia';
    if (hour < 18) return 'Boa tarde';
    return 'Boa noite';
  };

  // Topic label translation
  const getTopicLabel = (topic) => {
    const labels = {
      'travel': 'Viagens',
      'work': 'Trabalho',
      'movies': 'Filmes',
      'food': 'Comida',
      'hobbies': 'Hobbies',
      'family': 'Família',
      'free_chat': 'Conversa livre',
      'quick_practice': 'Prática rápida'
    };
    return labels[topic] || topic || 'Conversa';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-dark-bg flex items-center justify-center">
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="w-12 h-12 border-3 border-vocalira/30 border-t-vocalira rounded-full animate-spin mx-auto mb-4" />
          <p className="text-dark-muted text-sm">Carregando seu progresso...</p>
        </motion.div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-dark-bg flex items-center justify-center p-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-dark-card rounded-3xl p-8 shadow-dark-medium border border-dark-border max-w-sm w-full text-center"
        >
          <AlertCircle className="w-12 h-12 text-orange-400 mx-auto mb-4" />
          <h2 className="font-semibold text-dark-text text-lg mb-2">Ops! Algo deu errado</h2>
          <p className="text-dark-muted text-sm mb-6">Verifique sua conexão e tente novamente.</p>
          <Button onClick={() => { setLoading(true); fetchData(); }} className="bg-vocalira-gradient text-white rounded-full">
            <RefreshCw className="w-4 h-4 mr-2" />
            Tentar Novamente
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-bg" data-testid="dashboard">
      {/* Header with greeting */}
      <header className="bg-dark-card/80 backdrop-blur-md border-b border-dark-border sticky top-0 z-40">
        <div className="px-4 py-4 flex justify-between items-center max-w-lg mx-auto">
          <div className="flex items-center gap-3">
            <motion.img 
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 200 }}
              src="/logo.svg" 
              alt="Vocalira" 
              className="h-10 w-auto"
            />
            <div>
              <p className="text-xs text-dark-muted">{getGreeting()},</p>
              <h1 className="font-fraunces text-lg font-semibold text-dark-text">{firstName}!</h1>
            </div>
          </div>
          <Button onClick={handleLogout} variant="ghost" size="sm" className="text-dark-muted hover:text-dark-text">
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 py-6 pb-28 max-w-lg mx-auto space-y-5">
        
        {/* Welcome Card - First time */}
        <AnimatePresence>
          {showWelcomeCard && (
            <motion.div
              initial={{ opacity: 0, y: -20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.95 }}
              className="bg-gradient-to-r from-vocalira/30 to-cyan/20 rounded-3xl p-5 relative overflow-hidden border border-dark-border"
            >
              <button
                onClick={() => setShowWelcomeCard(false)}
                className="absolute top-3 right-3 text-dark-muted hover:text-dark-text transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="flex items-start gap-4">
                <motion.div 
                  initial={{ rotate: -10 }}
                  animate={{ rotate: [0, -10, 10, 0] }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  className="w-14 h-14 bg-dark-elevated rounded-2xl flex items-center justify-center shadow-sm flex-shrink-0 border border-dark-border"
                >
                  <span className="text-3xl">🎉</span>
                </motion.div>
                <div>
                  <h3 className="font-semibold text-dark-text">Parabéns, {firstName}!</h3>
                  <p className="text-dark-muted text-sm mt-1">
                    Você deu o primeiro passo. Pratique um pouquinho todo dia e veja sua evolução!
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ==================== MAIN CTA - ONE CLICK TO CHAT ==================== */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="space-y-3"
        >
          {/* Continue Conversation (if exists) - PRIMARY CTA */}
          {!loadingLastConv && lastConversation && (
            <motion.button
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleContinueConversation}
              className="w-full bg-vocalira-gradient rounded-3xl p-6 text-left shadow-glow relative overflow-hidden group"
              data-testid="continue-conversation-btn"
            >
              {/* Animated background */}
              <div className="absolute inset-0 bg-gradient-to-r from-vocalira via-purple-500 to-cyan bg-[length:200%_100%] group-hover:animate-shimmer" />
              
              <div className="relative flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <MessageCircle className="w-4 h-4 text-white" />
                    <span className="text-white/80 text-xs font-medium uppercase tracking-wider">
                      Continuar conversa
                    </span>
                  </div>
                  <h2 className="text-2xl font-semibold text-white mb-1">
                    Continuar com Lira
                  </h2>
                  <p className="text-white/70 text-sm">
                    {getTopicLabel(lastConversation.topic)} • {lastConversation.time_ago}
                  </p>
                  {lastConversation.last_message_preview && (
                    <p className="text-white/50 text-xs mt-2 line-clamp-1 italic">
                      &ldquo;{lastConversation.last_message_preview.substring(0, 50)}...&rdquo;
                    </p>
                  )}
                </div>
                <motion.div 
                  animate={{ x: [0, 5, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                  className="bg-white/20 rounded-2xl p-4 flex-shrink-0"
                >
                  <ArrowRight className="w-8 h-8 text-white" />
                </motion.div>
              </div>
            </motion.button>
          )}

          {/* New Conversation - SECONDARY or PRIMARY if no history */}
          <motion.button
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleMainCTA}
            disabled={startingChat}
            className={`w-full rounded-3xl p-6 text-left shadow-dark-soft relative overflow-hidden group transition-all ${
              lastConversation 
                ? 'bg-dark-card border border-dark-border' 
                : 'bg-vocalira-gradient'
            }`}
            data-testid="start-conversation-btn"
          >
            {!lastConversation && (
              <div className="absolute inset-0 bg-gradient-to-r from-vocalira via-purple-500 to-cyan bg-[length:200%_100%] group-hover:animate-shimmer" />
            )}
            
            <div className="relative flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Play className={`w-4 h-4 ${lastConversation ? 'text-vocalira' : 'text-white'}`} />
                  <span className={`text-xs font-medium uppercase tracking-wider ${
                    lastConversation ? 'text-dark-muted' : 'text-white/80'
                  }`}>
                    {lastConversation ? 'Nova conversa' : 'Começar agora'}
                  </span>
                </div>
                <h2 className={`text-xl font-semibold mb-1 ${
                  lastConversation ? 'text-dark-text' : 'text-white'
                }`}>
                  {lastConversation ? 'Conversar' : 'Conversar com Lira'}
                </h2>
                <p className={`text-sm ${lastConversation ? 'text-dark-muted' : 'text-white/70'}`}>
                  Pratique {targetLanguage} agora
                </p>
              </div>
              <motion.div 
                animate={startingChat ? { rotate: 360 } : { x: [0, 5, 0] }}
                transition={startingChat ? { duration: 1, repeat: Infinity } : { duration: 1.5, repeat: Infinity }}
                className={`rounded-2xl p-4 ${
                  lastConversation ? 'bg-vocalira/20' : 'bg-white/20'
                }`}
              >
                {startingChat ? (
                  <div className="w-8 h-8 border-3 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <MessageCircle className={`w-8 h-8 ${lastConversation ? 'text-vocalira' : 'text-white'}`} />
                )}
              </motion.div>
            </div>
          </motion.button>
        </motion.div>

        {/* Quick Actions - Conversar + Aprender */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-2 gap-3"
        >
          <button
            onClick={() => navigate('/scenarios')}
            className="bg-dark-card rounded-2xl p-4 border border-dark-border text-left active:scale-[0.98] transition-transform group hover:border-vocalira/30"
            data-testid="learn-btn"
          >
            <div className="w-10 h-10 bg-purple-500/20 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
              <BookOpen className="w-5 h-5 text-purple-400" />
            </div>
            <p className="font-medium text-dark-text text-sm">Aprender</p>
            <p className="text-xs text-dark-muted">Lições e cenários</p>
          </button>
          
          <button
            onClick={() => navigate('/conversations')}
            className="bg-dark-card rounded-2xl p-4 border border-dark-border text-left active:scale-[0.98] transition-transform group hover:border-cyan/30"
            data-testid="history-btn"
          >
            <div className="w-10 h-10 bg-cyan/20 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
              <Clock className="w-5 h-5 text-cyan" />
            </div>
            <p className="font-medium text-dark-text text-sm">Histórico</p>
            <p className="text-xs text-dark-muted">Conversas anteriores</p>
          </button>
        </motion.div>

        {/* Conversas Consecutivas Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="bg-gradient-to-br from-orange-500/20 via-orange-500/10 to-amber-500/10 rounded-3xl p-5 border border-orange-500/20"
          data-testid="streak-card"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <motion.div 
                animate={streak > 0 ? { scale: [1, 1.1, 1] } : {}}
                transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 2 }}
                className={`w-16 h-16 rounded-2xl flex items-center justify-center ${
                  streak > 0 ? 'bg-gradient-to-br from-orange-400 to-red-500' : 'bg-orange-500/20'
                }`}
              >
                <Flame className={`w-8 h-8 ${streak > 0 ? 'text-white' : 'text-orange-400'}`} />
              </motion.div>
              <div>
                <p className="text-xs text-orange-400/70 uppercase tracking-wider font-medium mb-1">Conversas Consecutivas</p>
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-bold text-orange-400">
                    <AnimatedNumber value={streak} />
                  </span>
                  <span className="text-orange-400/70 font-medium">dias</span>
                </div>
                <p className="text-orange-400/60 text-sm mt-1">
                  {streak === 0 ? 'Converse hoje para começar!' : 
                   streak === 1 ? 'Primeiro dia! Continue assim!' :
                   streak < 7 ? 'Você está no ritmo!' :
                   streak < 30 ? 'Incrível dedicação!' : 'Você é lendário!'}
                </p>
              </div>
            </div>
            {maxStreak > 0 && (
              <div className="text-right">
                <p className="text-xs text-orange-400/70">Recorde</p>
                <p className="text-lg font-bold text-orange-400">{maxStreak}</p>
              </div>
            )}
          </div>
        </motion.div>

        {/* Stats Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-3 gap-3"
        >
          {/* XP */}
          <div className="bg-dark-card rounded-2xl p-4 border border-dark-border text-center">
            <div className="w-10 h-10 bg-cyan/20 rounded-xl flex items-center justify-center mx-auto mb-2">
              <Zap className="w-5 h-5 text-cyan" />
            </div>
            <p className="text-xl font-bold text-dark-text"><AnimatedNumber value={xp} /></p>
            <p className="text-xs text-dark-muted">XP total</p>
          </div>
          
          {/* Words */}
          <div className="bg-dark-card rounded-2xl p-4 border border-dark-border text-center">
            <div className="w-10 h-10 bg-vocalira/20 rounded-xl flex items-center justify-center mx-auto mb-2">
              <BookOpen className="w-5 h-5 text-vocalira" />
            </div>
            <p className="text-xl font-bold text-dark-text"><AnimatedNumber value={wordsLearned} /></p>
            <p className="text-xs text-dark-muted">palavras</p>
          </div>
          
          {/* Minutes */}
          <div className="bg-dark-card rounded-2xl p-4 border border-dark-border text-center">
            <div className="w-10 h-10 bg-purple-500/20 rounded-xl flex items-center justify-center mx-auto mb-2">
              <Clock className="w-5 h-5 text-purple-400" />
            </div>
            <p className="text-xl font-bold text-dark-text"><AnimatedNumber value={minutesPracticed} /></p>
            <p className="text-xs text-dark-muted">minutos</p>
          </div>
        </motion.div>

        {/* Level Progress */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="bg-dark-card rounded-3xl p-5 border border-dark-border"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="relative">
                <ProgressRing progress={levelProgress} size={56} strokeWidth={5} color="#D946EF" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-lg font-bold text-dark-text">{level}</span>
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-dark-text">Nível {level}</h3>
                <p className="text-sm text-dark-muted">{levelName}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xs text-dark-muted">Próximo nível</p>
              <p className="text-sm font-medium text-vocalira">{100 - levelProgress}% restante</p>
            </div>
          </div>
          <div className="h-2.5 bg-dark-border rounded-full overflow-hidden">
            <motion.div 
              initial={{ width: 0 }}
              animate={{ width: `${levelProgress}%` }}
              transition={{ duration: 1, ease: "easeOut" }}
              className="h-full bg-vocalira-gradient rounded-full"
            />
          </div>
        </motion.div>

        {/* Weekly Progress */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-dark-card rounded-3xl p-5 border border-dark-border"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-dark-text flex items-center gap-2">
              <Calendar className="w-4 h-4 text-vocalira" />
              Esta semana
            </h3>
            <span className="text-xs text-dark-muted bg-dark-elevated px-2 py-1 rounded-full border border-dark-border">
              {conversationsCount} conversas
            </span>
          </div>
          <div className="flex justify-between items-end gap-2">
            {['D', 'S', 'T', 'Q', 'Q', 'S', 'S'].map((day, i) => {
              const isToday = i === new Date().getDay();
              const hasActivity = weeklyProgress[i] > 0;
              const height = Math.max(hasActivity ? weeklyProgress[i] : 8, 8);
              
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-2">
                  <motion.div 
                    initial={{ height: 0 }}
                    animate={{ height }}
                    transition={{ duration: 0.5, delay: i * 0.05 }}
                    className={`w-full max-w-[32px] rounded-t-lg ${
                      hasActivity 
                        ? 'bg-vocalira-gradient' 
                        : 'bg-dark-border'
                    } ${isToday ? 'ring-2 ring-vocalira ring-offset-2 ring-offset-dark-bg' : ''}`}
                    style={{ minHeight: 8 }}
                  />
                  <span className={`text-xs ${isToday ? 'text-dark-text font-bold' : 'text-dark-muted'}`}>
                    {day}
                  </span>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Daily Missions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="bg-dark-card rounded-3xl p-5 border border-dark-border"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-dark-text flex items-center gap-2">
              <Target className="w-4 h-4 text-cyan" />
              Missões de Hoje
            </h3>
            <div className="flex items-center gap-2">
              <div className="w-20 h-2 bg-dark-border rounded-full overflow-hidden">
                <motion.div 
                  initial={{ width: 0 }}
                  animate={{ width: `${missionsProgress}%` }}
                  transition={{ duration: 0.5 }}
                  className="h-full bg-vocalira-gradient rounded-full"
                />
              </div>
              <span className="text-xs font-medium text-dark-text">{completedMissions}/{totalMissions}</span>
            </div>
          </div>
          
          <div className="space-y-3">
            {dailyMissions?.missions?.map((mission, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 + i * 0.1 }}
                className={`flex items-center gap-3 p-3 rounded-xl transition-all ${
                  mission.completed ? 'bg-vocalira/20 border border-vocalira/30' : 'bg-dark-elevated border border-dark-border'
                }`}
              >
                <motion.div 
                  animate={mission.completed ? { scale: [1, 1.2, 1] } : {}}
                  className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    mission.completed ? 'bg-vocalira' : 'bg-dark-card border-2 border-dark-border'
                  }`}
                >
                  {mission.completed ? (
                    <motion.svg 
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: 1 }}
                      className="w-4 h-4 text-white" 
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </motion.svg>
                  ) : (
                    <Target className="w-3.5 h-3.5 text-dark-muted" />
                  )}
                </motion.div>
                <div className="flex-1">
                  <p className={`text-sm font-medium ${mission.completed ? 'text-dark-muted line-through' : 'text-dark-text'}`}>
                    {mission.title}
                  </p>
                </div>
                <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                  mission.completed ? 'bg-vocalira/30 text-vocalira-light' : 'bg-cyan/20 text-cyan'
                }`}>
                  +{mission.xp} XP
                </span>
              </motion.div>
            ))}
          </div>
          
          {completedMissions === totalMissions && (
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="mt-4 p-4 bg-gradient-to-r from-vocalira/30 to-cyan/20 rounded-2xl text-center border border-vocalira/30"
            >
              <motion.span 
                animate={{ rotate: [0, -10, 10, 0] }}
                transition={{ duration: 0.5, repeat: 2 }}
                className="text-2xl inline-block"
              >
                🎉
              </motion.span>
              <p className="text-sm font-medium text-dark-text mt-1">
                Todas as missões completas! +50 XP bônus!
              </p>
            </motion.div>
          )}
        </motion.div>

        {/* Motivational Footer */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center text-dark-muted text-sm pt-4"
        >
          Pratique um pouquinho todo dia. Pequenos passos, grandes resultados.
        </motion.p>
      </main>
    </div>
  );
};

export default Dashboard;
